// packages/ui/src/Badge.tsx

import React from 'react';
import clsx from 'clsx';
import { KeyBadgeV10 } from '@dopomogai/types';
import { useTranslation } from 'react-i18next';
import { renderIcon } from '@dopomogai/utils';

type TranslationNamespace = string | readonly string[];

type BadgeAccentColor =
  | 'blue'
  | 'green'
  | 'purple'
  | 'red'
  | 'orange'
  | 'slate'
  | 'yellow'
  | 'pink'
  | 'cyan'
  | 'gray'
  | 'indigo'
  | 'teal'
  | 'lime'
  | 'fuchsia'
  | 'violet'
  | 'emerald'
  | 'amber'
  | 'rose';

export interface BadgeProps extends Omit<KeyBadgeV10, 'actionType' | 'actionPayload' | 'text' | 'tooltip'> {
  text?: string;
  textKey?: string;
  tooltip?: string;
  tooltipKey?: string;
  onClick?: () => void;
  className?: string;
  accentColor?: BadgeAccentColor;
  size?: 'sm' | 'md';
  variant?: 'solid' | 'outline';
  translationNs?: TranslationNamespace;
}

const accentColorClasses: Record<BadgeAccentColor, { solid: string; outline: string }> = {
  blue: {
    solid: 'bg-sky-100 text-sky-800 border-sky-400',
    outline: 'bg-transparent text-sky-700 border-sky-400',
  },
  green: {
    solid: 'bg-green-100 text-green-800 border-green-400',
    outline: 'bg-transparent text-green-700 border-green-400',
  },
  purple: {
    solid: 'bg-purple-100 text-purple-800 border-purple-400',
    outline: 'bg-transparent text-purple-700 border-purple-400',
  },
  red: {
    solid: 'bg-red-100 text-red-800 border-red-400',
    outline: 'bg-transparent text-red-700 border-red-400',
  },
  orange: {
    solid: 'bg-orange-100 text-orange-800 border-orange-400',
    outline: 'bg-transparent text-orange-700 border-orange-400',
  },
  slate: {
    solid: 'bg-slate-100 text-slate-800 border-slate-400',
    outline: 'bg-transparent text-slate-700 border-slate-400',
  },
  gray: {
    solid: 'bg-slate-100 text-slate-800 border-slate-400',
    outline: 'bg-transparent text-slate-700 border-slate-400',
  },
  yellow: {
    solid: 'bg-yellow-100 text-yellow-800 border-yellow-400',
    outline: 'bg-transparent text-yellow-700 border-yellow-400',
  },
  pink: {
    solid: 'bg-pink-100 text-pink-800 border-pink-400',
    outline: 'bg-transparent text-pink-700 border-pink-400',
  },
  cyan: {
    solid: 'bg-cyan-100 text-cyan-800 border-cyan-400',
    outline: 'bg-transparent text-cyan-700 border-cyan-400',
  },
  indigo: {
    solid: 'bg-indigo-100 text-indigo-800 border-indigo-400',
    outline: 'bg-transparent text-indigo-700 border-indigo-400',
  },
  teal: {
    solid: 'bg-teal-100 text-teal-800 border-teal-400',
    outline: 'bg-transparent text-teal-700 border-teal-400',
  },
  lime: {
    solid: 'bg-lime-100 text-lime-800 border-lime-400',
    outline: 'bg-transparent text-lime-700 border-lime-400',
  },
  fuchsia: {
    solid: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-400',
    outline: 'bg-transparent text-fuchsia-700 border-fuchsia-400',
  },
  violet: {
    solid: 'bg-violet-100 text-violet-800 border-violet-400',
    outline: 'bg-transparent text-violet-700 border-violet-400',
  },
  emerald: {
    solid: 'bg-emerald-100 text-emerald-800 border-emerald-400',
    outline: 'bg-transparent text-emerald-700 border-emerald-400',
  },
  amber: {
    solid: 'bg-amber-100 text-amber-800 border-amber-400',
    outline: 'bg-transparent text-amber-700 border-amber-400',
  },
  rose: {
    solid: 'bg-rose-100 text-rose-800 border-rose-400',
    outline: 'bg-transparent text-rose-700 border-rose-400',
  },
};

export const Badge = React.forwardRef<HTMLElement, BadgeProps>(
  (
    {
      text: directText,
      textKey,
      icon,
      tooltip: directTooltip,
      tooltipKey,
      url,
      accentColor = 'gray',
      onClick,
      className,
      size = 'md',
      variant = 'solid', // Default to solid
      translationNs = ['public_showcase', 'solution_showcase'],
    },
    ref
  ) => {
    const { t } = useTranslation(translationNs);

    const resolvedText = textKey ? t(textKey) : directText;
    const resolvedTooltip = tooltipKey ? t(tooltipKey) : directTooltip;
    const isInteractive = !!(onClick || url);
    const accessibleLabel = typeof resolvedText === 'string' ? resolvedText : resolvedTooltip;

    const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm';
    const colorClasses = (accentColorClasses[accentColor] ?? accentColorClasses.gray)[variant];

    const commonClasses = clsx(
      // Base structure
      'inline-flex items-center gap-1.5 font-medium leading-tight',
      sizeClasses,
      colorClasses,

      // Neobrutalism
      'rounded-full border',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-emerald-500',

      // Liquid glass overrides
      '[.theme-liquid-glass_&]:border-white/30 [.theme-liquid-glass_&]:backdrop-blur-sm',
      '[.theme-liquid-glass_&]:focus-visible:ring-violet-400',

      className
    );

    const interactiveEffectClasses = isInteractive
      ? 'transition-transform duration-150 ease-out hover:scale-105 active:scale-100'
      : undefined;

    const BadgeContent = () => (
      <>
        {icon && <span className="flex items-center">{renderIcon(icon, { size: 12 })}</span>}
        {resolvedText && <span>{resolvedText}</span>}
      </>
    );

    if (url && !onClick) {
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={url}
          className={clsx(commonClasses, interactiveEffectClasses)}
          title={resolvedTooltip}
          aria-label={!resolvedText ? accessibleLabel : undefined}
          target="_blank"
          rel="noopener noreferrer"
        >
          <BadgeContent />
        </a>
      );
    }

    if (onClick) {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          className={clsx(commonClasses, 'cursor-pointer', interactiveEffectClasses)}
          title={resolvedTooltip}
          aria-label={!resolvedText ? accessibleLabel : undefined}
          onClick={onClick}
        >
          <BadgeContent />
        </button>
      );
    }

    return (
      <span
        ref={ref as React.Ref<HTMLSpanElement>}
        className={commonClasses}
        title={resolvedTooltip}
      >
        <BadgeContent />
      </span>
    );
  }
);

Badge.displayName = 'Badge';

export type { BadgeAccentColor };
export default Badge;
